
/**
 * Created by greg on 12/07/2018.
 */
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, combineLatest, lastValueFrom } from 'rxjs';
import { EventStreamChange } from '../models/$event.model';
import { ErrorStreamChange } from '../models/$error.model';
import { takeUntil, map, take, filter } from 'rxjs/operators';
import { UserService } from './user.service';
import { LogService} from './log.service';
import { UiService } from "./ui.service";
import { Store } from '@ngrx/store';
import { OnReset, Transitions } from './transition.service.helpers';
import { TipType, UserSettings } from '../../../globals/store/user.data';
import { userFeature } from '../store/user.era';
import { ActivatedRoute, ActivationEnd, NavigationEnd, Router } from '@angular/router';
import { doc, Firestore, onSnapshot, updateDoc } from '@angular/fire/firestore';
import { settingsActions, settingsFeature } from '../store/settings.era';


@Injectable({ providedIn: 'root'})
export class SettingsService implements OnReset, OnDestroy {

    readonly _file = 'settings.service.ts'
    public UPDATE_CLOSED = 'update closed';
    public CURRENT_SETTINGS_COMPILED = 'current_settings_compiled';
    public DEFAULT_SETTINGS_UPDATE = "default_settings_update";
    public settings;
    public update_zoom_timeout;
    public notifications_set;

    public $resetSettings: Subject<boolean> = new Subject();
    public $destroy: Subject<boolean> = new Subject();
    public $events: Subject<EventStreamChange> = new Subject();
    public $errors: Subject<ErrorStreamChange> = new Subject();
    private db = inject(Firestore);

    public bank = {
        user: {
            event_order_key: 'first_date',
            event_order_rev: true,
            currency: 'AUD'
        } as UserSettings,
        account: {}};
    /*
    * the idea of priority is so that we can determine who has the final say. If an account holder has a setting, and a
    * user has a preference that is different, the session will know which one takes priority from this variable.
    * OR you might have a user setting that you want to have different preferences for one account
    * */
    public priority = {
        email_promo_planit: ['user'],
        email_postevent_reports: ['preference', 'account'],
        email_monthly_receipts: ['account'],
        hide_closed_events: ['preference', 'user'],
        background_color: ['user', 'preference', 'account'],
        banana: ['user', 'preference', 'account'],
    };

    public defaults = {
        event_order_key: 'first_date',
        event_order_rev: true,
        event_show_closed: false,
        currency: 'AUD',
        country: 'AU'
    };

    // comes from functions/src/app/system.assets.ts via default_settigns link
    public notification_settings;

    onReset(resetType, value = null) {
      switch (resetType) {
        case Transitions.OnEnterUser:
          return this.subscribeToSettings()
        case Transitions.OnExitUser:
          return this.$resetSettings.next(true)
        default:
          break;
      }
    }

    subscribeToSettings() {
      onSnapshot(doc(this.db, '_system', 'settings'), (snapshot) => {
        const settings = snapshot.data()
        if(!settings || !settings['notification']) return;
        this.notification_settings = settings['notification'];
        this.backfillNotifDefaults()
      },
      (response)=>{
        this.log.error({file:'settings.service.ts', function:'constructor',response,error:new Error()})
      })
    }

    constructor( public log: LogService, 
      //private activatedRoute: ActivatedRoute,
      private router: Router,
        public userService: UserService, 
        public ui:UiService, public store: Store
    ) {
      this.store.select(userFeature.selectUser).pipe(takeUntil(this.$destroy)).subscribe(user=>{
        const user_settings: UserSettings = user && user.settings ? {...user.settings} : null;
        // this.activatedRoute.params.pipe(take(1)).subscribe(root=>{
        //   // this happens when user settings initiates after the router is set <== except user settings can be null for whatever reason
        //   this.manageSettings(user_settings, root);
        // })
        this.router.events.pipe(
          takeUntil(this.$destroy),
          //take(1),
          filter(event => event instanceof ActivationEnd),
          map((event: ActivationEnd) => event.snapshot.params),
        ).subscribe(params=>{
          this.manageSettings(user_settings, params)
        })
      })
    }
    manageSettings(user_settings: UserSettings, params){ 
      // if there are any settings to get other than user          if(!user_settings) return;
      if(params && params.id){
        let type = params.id
        if (type === '') type = 'card'
        console.log({ type })
        if(user_settings.homepage !== type){
          user_settings.homepage = type
        }
      }
      // console.log('user_settings ', user_settings);
      
      const updated_settings = {...user_settings};
      if(updated_settings.notifs && !this.notifications_set){
        const notifs = this.populateNotifDefaults({...updated_settings.notifs})
        updated_settings.notifs = notifs;
        this.notifications_set = true;
      }
      this.store.dispatch(settingsActions.updateUserSettings(updated_settings))

    }
    saveUserSetting<T extends keyof UserSettings>(key: T, value: UserSettings[T]){
      const update_obj = {['settings.'+key]:value}
      updateDoc(doc(this.db, 'users', this.userService.uid), update_obj)
      .catch(response=>{
        this.log.error({file:this._file, function:'saveUserSetting', data:{key,value}, message:'There was an error saving your setting',response})
      })
    }

    async resetColumnDefaults() {
      const settings = await lastValueFrom(this.store.select(settingsFeature.selectUser).pipe(take(1))) as UserSettings
      const update = {} as Partial<UserSettings>
      Object.keys(settings)
        .filter(k => k.includes('_columns'))
        .forEach(k => update[`settings.${k}`] = null)
      console.log({ update, uid: this.userService.uid })
      updateDoc(doc(this.db, 'users', this.userService.uid), update)
        .catch(response=>{
          this.log.error({file:this._file, function:'saveUserSetting', data: update, message:'There was an error resetting column defaults',response})
        })
    }

    async hideTipType(tipType: TipType) {
      const settings = await lastValueFrom(this.store.select(settingsFeature.selectUser).pipe(take(1))) as UserSettings
      this.saveUserSetting('hiddenTips', [...(settings.hiddenTips || []), tipType])
    }

    async resetTips() {
      this.saveUserSetting('hiddenTips', [])
    }

    // get current user.settings and save to settings.user
    backfillNotifDefaults(){
      this.store.select(userFeature.selectUser).pipe(take(1)).subscribe(user=>{
        const notif_store= user.settings.notifs || {};
        const notifs = this.populateNotifDefaults({...notif_store})
        if(JSON.stringify(notif_store) === JSON.stringify(notifs)) return;
        this.store.dispatch( settingsActions.updateUserSetting('notifs', notifs ))
      })
    }

    // return merged notif defaults, possibly a saved_notif and passed notif settings
    populateNotifDefaults(notifs, saved_notifs = {}){
      if(!this.notification_settings) return notifs;
      let keys = [];
      this.notification_settings._order.forEach( ord =>{ keys = [...keys, ...ord.order] })
      const aves = this.notification_settings._notification_avenues;
      keys.filter(key=>!/^_/.test(key)).forEach(key=>{
        if(!notifs[key]) {
          notifs[key] = saved_notifs && saved_notifs[key] ? saved_notifs[key] : {};
        }
        aves.forEach(ave=>{
          if(!notifs[key].hasOwnProperty(ave.handle)){
            if(saved_notifs && saved_notifs[key] && saved_notifs[key].hasOwnProperty(ave.handle)){
              notifs[key][ave.handle] = saved_notifs[key][ave.handle];
            }else{
              notifs[key][ave.handle] = this.notification_settings[key].default[ave.handle]
            }
          }
        })
      })
      return notifs
    }
    handleUpdates(change) {
        switch (change.action) {
            case this.ui.ZOOM_CHANGE:
                if(!change.data.from_init) this.didZoom(change);
        }
    }
    didZoom(change){
      const self = this;
      clearTimeout(self.update_zoom_timeout);
      this.update_zoom_timeout = setTimeout(_=>{
        self.store.dispatch(settingsActions.updateUserSetting('zoom',change.data.zoomAmount))
        // self.updateUserSettings('zoom',change.data.zoomAmount);
      },3000)
    }

    ngOnDestroy(): void {
      this.$destroy.next(true);
      this.$destroy.complete();
    }

}
